import { useContext } from 'react';
import clsx from 'clsx';

import WorkTemplateContext from '@atom/components/workTemplate/WorkTemplateContext';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Icon, Select } from '@atom/mui';
import { CategoryIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { InventorySchemaItem } from '@atom/types/schema';
import { InheritedComponentType } from '@atom/types/workTemplate';
import schemaUtilities from '@atom/utilities/schemaUtilities';
import {
  isComponentInherited,
  rollUpTaskInheritedComponent,
  WorkTypeVariant,
} from '@atom/utilities/workTemplateUtilities';

import classes from './workTypeEditModal.module.css';

const { MenuItem } = Select;

const EMPTY_SCHEMA = 'empty';

const styles = {
  textFieldStyle: {
    marginLeft: '1rem',
    color: colors.neutral.dark,
  },
  icon: {
    marginTop: '1rem',
  },
};

interface Props {
  schemaId: string;
  setSchemaId: (schemaId: string) => void;
}

const WorkTemplateSchemaSelect = ({ schemaId, setSchemaId }: Props) => {
  const { workTemplate, workTypeVariant } = useContext(WorkTemplateContext);

  const { schemas = [] } = useSchemas();

  const isSchemaInherited = isComponentInherited(
    InheritedComponentType.TASK_SCHEMA,
    rollUpTaskInheritedComponent(
      workTemplate,
      InheritedComponentType.TASK_SCHEMA,
    ),
    workTemplate?.schemaId,
  );

  const isSchemaDisabled =
    workTemplate.published ||
    (workTypeVariant === WorkTypeVariant.CHILD && isSchemaInherited);

  const schemaHelperText =
    workTypeVariant === WorkTypeVariant.CHILD && isSchemaInherited
      ? 'Inherited from the parent work template'
      : '';

  return (
    <div className={classes.modalRow}>
      <Icon
        color={colors.neutral.gray}
        style={styles.icon}
        icon={CategoryIcon}
      />
      <Select
        value={schemaId}
        style={styles.textFieldStyle}
        onChange={event => setSchemaId(String(event.target.value))}
        label="Asset Type"
        fullWidth
        disabled={isSchemaDisabled}
        helperText={schemaHelperText}
      >
        <MenuItem key="empty" value={EMPTY_SCHEMA}>
          <div className={classes.schemaMenuItemContainer}>
            <div className={classes.emptyIcon}>-</div>
            <div className={clsx(classes.schemaMenuItemTitle, classes.empty)}>
              All Asset Types
            </div>
          </div>
        </MenuItem>
        {schemas.map((schema: InventorySchemaItem) => {
          const icon = schemaUtilities.getSchemaIconFromSchemaOrAsset(schema);

          return (
            <MenuItem key={schema.id} value={schema.id}>
              <div className={classes.schemaMenuItemContainer}>
                {icon}
                <div className={classes.schemaMenuItemTitle}>{schema.name}</div>
              </div>
            </MenuItem>
          );
        })}
      </Select>
    </div>
  );
};

export default WorkTemplateSchemaSelect;
